import type { ReactElement } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import ProgressSpinner from "@/Shared/Icons/ProgressSpinner";
import { ProgressSpinnerSizes } from "@/Shared/Icons/ProgressSpinnerSizes";
import { BigCommerceAdminApi } from "../../Apis/BigCommerceAdminApi";
import { apiServerUrl } from "../../Environment";
import { useAuthentication } from "@/Slices/authenticationSlice";
import {
  isAllowedBigCommerceAppOrigin,
  isBigCommerceRetryable,
  bigCommerceMerchantMessage,
  requestBigCommerceSession,
} from "./Helpers/bigCommerceConnectSession";
import {
  BigCommerceConnectCard_,
  BigCommerceConnectSpinnerRow_,
} from "./Internal/BigCommerceConnectCard_";
// Cross-feature import of the canonical sign-in form, reused rather than duplicating auth logic so
// the merchant can sign in inside the popup — same reasoning as the Shopify connect screen.
import { AuthenticationSignIn } from "@/Shared/Authentication/CustomComponents/AuthenticationSignIn";

/**
 * The popup the BigCommerce embedded app opens at /connect-bigcommerce. Runs on the Dashboard's own
 * origin, which is the whole point: that is where the merchant's FreightSimple session lives.
 *
 * Flow:
 *   1. Read `?appOrigin=...` and validate it against an allowlist.
 *   2. If not signed in, embed the sign-in form here rather than sending them elsewhere.
 *   3. Ask the app for its session token, which names the store.
 *   4. Confirm, then POST /integrations/bigcommerce/admin-api/link with the FS token and that
 *      session token — dual-token: one says which company, the other which store.
 *   5. Post the result back to the app and close.
 *
 * The `appOrigin` validation is load-bearing. Without it, luring a signed-in Dashboard user to
 * `/connect-bigcommerce?appOrigin=https://attacker.example` would hand an attacker a live session
 * token for a merchant's store.
 */

type ScreenState =
  | { kind: "invalid_origin"; reason: string }
  | { kind: "no_opener" }
  | { kind: "needs_signin" }
  | { kind: "waiting_for_session" }
  | { kind: "session_error"; message: string }
  | { kind: "ready_to_link"; storeHash: string | undefined }
  | { kind: "submitting" }
  | { kind: "linked" }
  | { kind: "failed"; code: string; message: string; canRetry: boolean };

function ConnectBigCommerceScreen() {
  const { isAuthenticated, accessToken } = useAuthentication();

  const appOriginFromQuery = useMemo(() => {
    return new URLSearchParams(window.location.search).get("appOrigin");
  }, []);

  const initialState = useMemo<ScreenState>(() => {
    if (!appOriginFromQuery) {
      return {
        kind: "invalid_origin",
        reason: "Missing appOrigin query parameter.",
      };
    }
    if (
      !isAllowedBigCommerceAppOrigin(
        appOriginFromQuery,
        window.location.hostname,
      )
    ) {
      return {
        kind: "invalid_origin",
        reason: `App origin ${appOriginFromQuery} is not on the allowlist.`,
      };
    }
    if (!window.opener) return { kind: "no_opener" };
    if (!isAuthenticated) return { kind: "needs_signin" };
    return { kind: "waiting_for_session" };
  }, [appOriginFromQuery, isAuthenticated]);

  const [state, setState] = useState<ScreenState>(initialState);

  // Re-sync when auth flips, so signing in inside the popup advances the flow without a reload.
  useEffect(() => {
    setState(initialState);
  }, [initialState]);

  const appOrigin = appOriginFromQuery ?? "";
  const [sessionToken, setSessionToken] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (state.kind !== "waiting_for_session") return;
    let cancelled = false;
    void (async () => {
      try {
        const { token, storeHash } = await requestBigCommerceSession(appOrigin);
        if (cancelled) return;
        // Held rather than re-requested at click time: unlike Shopify's one-minute ID token, this
        // one lives an hour, so the human pause before the button is not racing its expiry.
        setSessionToken(token);
        setState({ kind: "ready_to_link", storeHash });
      } catch (e) {
        if (!cancelled) {
          setState({ kind: "session_error", message: (e as Error).message });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state.kind, appOrigin]);

  const handleRetry = useCallback(() => {
    setState({ kind: "waiting_for_session" });
  }, []);

  const fail = useCallback(
    (code: string, backendMessage: string) => {
      const message = bigCommerceMerchantMessage(code, backendMessage);
      window.opener?.postMessage(
        {
          source: "freightsimple-connect-bigcommerce",
          ok: false,
          error: code,
          message,
        },
        appOrigin,
      );
      setState({
        kind: "failed",
        code,
        message,
        canRetry: isBigCommerceRetryable(code),
      });
    },
    [appOrigin],
  );

  const handleConnect = useCallback(async () => {
    if (state.kind !== "ready_to_link") return;
    if (!accessToken || sessionToken === undefined) return;
    setState({ kind: "submitting" });

    const api = new BigCommerceAdminApi(apiServerUrl(), accessToken);
    // postLink reports 4xx and 5xx as results, but a network-level failure still throws. Unhandled,
    // that leaves the merchant on a spinner with no way out.
    let result;
    try {
      result = await api.postLink({ sessionToken });
    } catch (e) {
      fail("link_request_failed", (e as Error).message);
      return;
    }

    if (result.status === 200 && result.body.success) {
      window.opener?.postMessage(
        { source: "freightsimple-connect-bigcommerce", ok: true },
        appOrigin,
      );
      setState({ kind: "linked" });
      // A moment for the app to receive the message before this window goes away.
      window.setTimeout(() => window.close(), 750);
    } else {
      fail(
        result.body.error ?? `http_${result.status}`,
        result.body.message ??
          `Unexpected error connecting this store (HTTP ${result.status}).`,
      );
    }
  }, [state, accessToken, sessionToken, appOrigin, fail]);

  return (
    <BigCommerceConnectCard_ hideLogo={state.kind === "needs_signin"}>
      {renderBody(state, handleConnect, handleRetry)}
    </BigCommerceConnectCard_>
  );
}

function LoadingBody_({ label }: { label: string }): ReactElement {
  return (
    <Stack gap={4} align="center">
      <BigCommerceConnectSpinnerRow_>
        <ProgressSpinner size={ProgressSpinnerSizes.Medium} />
      </BigCommerceConnectSpinnerRow_>
      <T size="base" color="secondary">
        {label}
      </T>
    </Stack>
  );
}

function ErrorText_({ children }: { children: string }): ReactElement {
  return (
    <T size="lg" weight="medium" color="red">
      {children}
    </T>
  );
}

function renderBody(
  state: ScreenState,
  onConnect: () => void,
  onRetry: () => void,
): ReactElement {
  switch (state.kind) {
    case "invalid_origin":
      return (
        <Stack gap={3} align="center">
          <Heading level={1}>This page can&apos;t be opened directly</Heading>
          <T size="base" color="secondary">
            Open the FreightSimple app from your BigCommerce control panel and
            press Connect there.
          </T>
        </Stack>
      );

    case "no_opener":
      return (
        <Stack gap={3} align="center">
          <Heading level={1}>This window lost its connection</Heading>
          <T size="base" color="secondary">
            Close it and press Connect again in the FreightSimple app.
          </T>
        </Stack>
      );

    case "needs_signin":
      // The auth state flip re-runs initialState, which advances the flow — so there is nothing to
      // do onSuccess, and no redirect, because this window is a popup that has to stay put.
      return (
        <AuthenticationSignIn
          redirectAfterSignin={false}
          onSuccess={() => {}}
          onForgotPassword={() =>
            window.open(
              "/request-reset-password",
              "_blank",
              "noopener,noreferrer",
            )
          }
          onCreateAccountInstead={() =>
            window.open("/create-account", "_blank", "noopener,noreferrer")
          }
        />
      );

    case "waiting_for_session":
      return <LoadingBody_ label="Checking which store you're connecting…" />;

    case "session_error":
      return (
        <Stack gap={4} align="center">
          <ErrorText_>{state.message}</ErrorText_>
          <Button onClick={onRetry}>Try again</Button>
        </Stack>
      );

    case "ready_to_link":
      return (
        <Stack gap={4} align="center">
          <Heading level={1}>Connect FreightSimple</Heading>
          <T size="base" color="secondary">
            {state.storeHash === undefined
              ? "Connect this BigCommerce store to your FreightSimple account?"
              : `Connect BigCommerce store ${state.storeHash} to your FreightSimple account?`}
          </T>
          <Button primary onClick={onConnect}>
            Connect
          </Button>
        </Stack>
      );

    case "submitting":
      return <LoadingBody_ label="Connecting…" />;

    case "linked":
      return (
        <Stack gap={3} align="center">
          <Heading level={1}>Connected</Heading>
          <T size="base" color="secondary">
            You can close this window.
          </T>
        </Stack>
      );

    case "failed":
      return (
        <Stack gap={4} align="center">
          <ErrorText_>{state.message}</ErrorText_>
          {state.canRetry && <Button onClick={onRetry}>Try again</Button>}
        </Stack>
      );
  }
}

export default ConnectBigCommerceScreen;
